/**
 * Prompt Composer Utility
 *
 * Handles prompt composition via pure template slot replacement.
 */

import type { GeneratedVariation, SelectedAttribute } from './variationGenerator'

// ── Types ───────────────────────────────────────────────────────────────────

export interface ComposedPrompt {
  index: number
  variationHash: string
  prompt: string
  negativePrompt: string
  status: 'pending' | 'composed' | 'manual-edit' | 'flagged'
  manualOverride?: string
  tokenCount: number
  composedAt?: string
}

export interface CompositionStats {
  totalComposed: number
  totalTokensUsed: number
  averageTokensPerPrompt: number
  manualEdits: number
  flaggedForReview: number
}

export interface ComposerConfig {
  systemPrompt: string
  template: string
  temperature: number
  maxTokensPerPrompt: number
  negativePromptTemplate: string
}

// ── Token estimation ────────────────────────────────────────────────────────

/** Rough token estimate: ~4 chars per token for English text */
export function estimateTokens(text: string): number {
  if (!text) return 0
  return Math.ceil(text.length / 4)
}

// ── Slot replacement ────────────────────────────────────────────────────────

/**
 * Replace {category_name} slots in a template with attribute values.
 * Uses the attribute's promptFragment if available, otherwise optionName.
 * Category names are matched case-insensitively with spaces/underscores normalized.
 */
function normalizeSlotName(name: string): string {
  return name.toLowerCase().replace(/[\s_-]+/g, '_')
}

export function fillTemplate(template: string, attributes: SelectedAttribute[]): string {
  let result = template

  // Build lookup map: normalized category name → value
  const lookup = new Map<string, string>()
  for (const attr of attributes) {
    const value = attr.promptFragment || attr.optionName || ''
    lookup.set(normalizeSlotName(attr.categoryName), value)
  }

  // Replace all {slot_name} patterns
  result = result.replace(/\{([^}]+)\}/g, (_match, slotName: string) => {
    const normalized = normalizeSlotName(slotName)
    return lookup.get(normalized) ?? `{${slotName}}`
  })

  return result
}

// ── Compose functions ───────────────────────────────────────────────────────

/** Fill negative prompt template with attributes */
export function composeNegativePrompt(
  negativeTemplate: string,
  attributes: SelectedAttribute[],
): string {
  if (!negativeTemplate) return ''
  return fillTemplate(negativeTemplate, attributes)
}

// ── Pure template composition ──────────────────────────────────────────────

export function composePureTemplate(
  variation: GeneratedVariation,
  config: ComposerConfig,
): ComposedPrompt {
  const prompt = config.template
    ? fillTemplate(config.template, variation.attributes)
    : variation.attributes
        .filter((a) => a.optionName !== null)
        .map((a) => a.promptFragment || a.optionName)
        .join('. ')

  const negativePrompt = composeNegativePrompt(config.negativePromptTemplate, variation.attributes)

  return {
    index: variation.index,
    variationHash: variation.hash,
    prompt,
    negativePrompt,
    status: 'composed',
    tokenCount: estimateTokens(prompt),
    composedAt: new Date().toISOString(),
  }
}

// ── Batch composition ───────────────────────────────────────────────────────

/**
 * Compose prompts via pure template slot replacement (all client-side, no LLM).
 */
export async function composeAllPureTemplate(
  variations: GeneratedVariation[],
  config: ComposerConfig,
  onProgress?: (composed: number, total: number) => void,
): Promise<{ prompts: ComposedPrompt[]; stats: CompositionStats }> {
  const prompts: ComposedPrompt[] = []
  const CHUNK_SIZE = 200

  for (let i = 0; i < variations.length; i++) {
    prompts.push(composePureTemplate(variations[i], config))

    if ((i + 1) % CHUNK_SIZE === 0) {
      onProgress?.(i + 1, variations.length)
      await new Promise<void>((resolve) => setTimeout(resolve, 0))
    }
  }

  onProgress?.(variations.length, variations.length)

  const totalTokens = prompts.reduce((s, p) => s + p.tokenCount, 0)

  return {
    prompts,
    stats: {
      totalComposed: prompts.length,
      totalTokensUsed: totalTokens,
      averageTokensPerPrompt: prompts.length > 0 ? Math.round(totalTokens / prompts.length) : 0,
      manualEdits: 0,
      flaggedForReview: 0,
    },
  }
}

/**
 * Extract category slot names from a template string.
 * Returns array of slot names found in {brackets}.
 */
export function extractTemplateSlots(template: string): string[] {
  const slots: string[] = []
  const regex = /\{([^}]+)\}/g
  let match
  while ((match = regex.exec(template)) !== null) {
    slots.push(match[1])
  }
  return slots
}
